// client/src/components/pages/LexiStar/Drivers/Drivers.jsx
//
// LexiStar drivers (ST-00188 M3; LS-09 §5.9, §5.14; FR-DRV-001/002/003/005,
// FR-NTF-011). Route /lexistar/drivers, page permission lexistar.drivers.view;
// adding, editing, consent, allocation and import need lexistar.drivers.manage.
// Data: server/modules/lexistar/drivers (m3-contracts §7.2).
//
//  - Drivers: the register (search, active filter) with how each driver can
//    be reached (number + opt-in, Telegram link) and their truck now.
//  - Allocation board: trucks × days with driver spans, conflicts, the
//    unallocated list and "Who was driving?".
//  - A row opens the driver's profile drawer (?driver=<id>): consents with
//    evidence, allocation timeline + editor, recent messages.
//  - CSV import with a server-side dry run first.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaIdCard, FaPlus, FaFileImport, FaSearch } from 'react-icons/fa';
import SegmentedControl from '../../../common/SegmentedControl';
import ThemedSelect from '../../../common/ThemedSelect';
import { useAuth } from '../../../../context/AuthContext';
import { listAssets } from '../../../../services/lexistarAssets';
import { pickList } from '../../../../services/lexistarStatusBoard';
import {
  PageHeader,
  ErrorBanner,
  NoticeBanner,
  NoPermissionPage,
  LoadingState,
  EmptyState,
  inputClasses,
  cardClasses,
} from '../StatusBoard/atoms';
import { ChannelBadge } from '../Messages/MessageAtoms';
import { listDrivers } from './driversApi';
import { errorText } from './DriverDialog';
import DriverDialog from './DriverDialog';
import DriverDetail from './DriverDetail';
import ImportDialog from './ImportDialog';
import AllocationBoard from './AllocationBoard';
import {
  DRIVER_CHANNELS,
  CONSENT_META,
  consentOf,
  reachableChannels,
  languageLabel,
} from './driverLogic';

const TABS = [
  { value: 'drivers', label: 'Drivers' },
  { value: 'board', label: 'Allocation board', shortLabel: 'Board' },
];

function ConsentCell({ driver }) {
  return (
    <span className="flex flex-wrap gap-1">
      {DRIVER_CHANNELS.map((c) => {
        const state = consentOf(driver, c.value)?.state;
        if (!state && c.value !== 'telegram') return null;
        if (c.value === 'telegram' && !driver.telegram_chat_id && !state) return null;
        const meta =
          c.value === 'telegram' && !state
            ? { glyph: '✔', label: 'linked', className: 'bg-slate-100 text-slate-700' }
            : CONSENT_META[state];
        return (
          <span
            key={c.value}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${meta.className}`}
            title={`${c.label}: ${meta.label}`}
          >
            <ChannelBadge channel={c.value} compact />
            <span aria-hidden="true">{meta.glyph}</span>
            <span className="sr-only">{meta.label}</span>
          </span>
        );
      })}
    </span>
  );
}

const Drivers = () => {
  const { hasPermission } = useAuth();
  const canView =
    hasPermission('lexistar.drivers.view') || hasPermission('lexistar.drivers.manage');
  const canManage = hasPermission('lexistar.drivers.manage');
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'board' ? 'board' : 'drivers';
  const openId = params.get('driver');

  const [drivers, setDrivers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState('');
  const [active, setActive] = useState('true');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(undefined);
  const [importOpen, setImportOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const setParam = useCallback(
    (patch) => {
      const next = new URLSearchParams(params);
      Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const res = await listDrivers({ limit: 500, ...(active !== 'all' ? { active } : {}) });
      setDrivers(pickList(res, 'drivers'));
      setError(null);
    } catch (err) {
      setError(
        err.response?.status === 503
          ? 'Drivers are not installed on this server yet.'
          : errorText(err, 'Failed to load drivers.')
      );
    } finally {
      setLoading(false);
    }
  }, [canView, active]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  useEffect(() => {
    if (!canView) return;
    listAssets({ limit: 200 })
      .then((res) => setAssets(pickList(res, 'assets').filter((a) => a.asset_type !== 'trailer')))
      .catch(() => setAssets([]));
  }, [canView]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return drivers;
    return drivers.filter((d) =>
      [d.name, d.tag, d.phone_e164].some((v) =>
        String(v || '')
          .toLowerCase()
          .includes(needle)
      )
    );
  }, [drivers, q]);

  const header = (
    <PageHeader
      icon={FaIdCard}
      title="Drivers"
      subtitle="Who drives which truck, and how to reach them."
      toolbar={
        canView ? (
          <SegmentedControl
            options={TABS}
            value={tab}
            onChange={(v) => setParam({ tab: v === 'board' ? 'board' : null })}
            ariaLabel="Drivers view"
          />
        ) : null
      }
      actions={
        canManage ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              <FaFileImport size={11} /> Import CSV
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm"
            >
              <FaPlus size={11} /> New driver
            </button>
          </div>
        ) : null
      }
    />
  );

  if (!canView) {
    return (
      <NoPermissionPage header={header} message="You do not have permission to view drivers." />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-5 space-y-4">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
        <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />

        {tab === 'drivers' ? (
          <section className={cardClasses} aria-label="Driver register">
            <div className="px-4 py-3 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative w-full sm:w-72">
                <FaSearch
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={12}
                />
                <label htmlFor="drivers-search" className="sr-only">
                  Search drivers
                </label>
                <input
                  id="drivers-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Name, tag or number"
                  className={`${inputClasses} pl-8`}
                />
              </div>
              <div className="w-full sm:w-44">
                <ThemedSelect
                  aria-label="Status"
                  value={active}
                  onChange={(e) => setActive(e.target.value)}
                  className={inputClasses}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                  <option value="all">All drivers</option>
                </ThemedSelect>
              </div>
              <p className="sm:ml-auto text-xs text-slate-500">{visible.length} shown</p>
            </div>
            {loading && drivers.length === 0 ? (
              <LoadingState label="Loading drivers..." />
            ) : visible.length === 0 ? (
              <EmptyState
                icon={FaIdCard}
                title={drivers.length ? 'No driver matches' : 'No drivers yet'}
              >
                {drivers.length
                  ? 'Try another name, tag or number.'
                  : canManage
                    ? 'Add drivers one by one or import a CSV from your HR or payroll system.'
                    : 'Ask a fleet manager to add the drivers.'}
              </EmptyState>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" aria-label="Drivers">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs text-slate-500">
                      <th className="px-4 py-2 font-medium">Name</th>
                      <th className="px-4 py-2 font-medium">Tag</th>
                      <th className="px-4 py-2 font-medium">Language</th>
                      <th className="px-4 py-2 font-medium">Mobile</th>
                      <th className="px-4 py-2 font-medium">Channels</th>
                      <th className="px-4 py-2 font-medium">Truck now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((d) => {
                      const reach = reachableChannels(d);
                      return (
                        <tr
                          key={d.id}
                          className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${d.active === false ? 'text-slate-400' : ''}`}
                          onClick={() => setParam({ driver: d.id })}
                        >
                          <td className="px-4 py-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setParam({ driver: d.id });
                              }}
                              className="font-medium text-slate-900 hover:underline text-left"
                            >
                              {d.name}
                            </button>
                            {d.active === false && <span className="ml-2 text-xs">inactive</span>}
                            {d.active !== false && reach.length === 0 && (
                              <span className="block text-xs text-amber-700">
                                Not reachable yet
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 font-mono text-xs">{d.tag || '—'}</td>
                          <td className="px-4 py-2">{languageLabel(d.language)}</td>
                          <td className="px-4 py-2 tabular-nums">{d.phone_e164 || '—'}</td>
                          <td className="px-4 py-2">
                            <ConsentCell driver={d} />
                          </td>
                          <td className="px-4 py-2 font-mono text-xs">
                            {d.current_allocation?.registration || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ) : (
          <AllocationBoard
            assets={assets}
            drivers={drivers}
            canManage={canManage}
            onOpenDriver={(id) => setParam({ driver: id })}
            reloadKey={reloadKey}
          />
        )}
      </div>

      {openId && (
        <DriverDetail
          key={`${openId}:${reloadKey}`}
          driverId={openId}
          canManage={canManage}
          assets={assets}
          onClose={() => setParam({ driver: null })}
          onEdit={(d) => setEditing(d)}
          onChanged={() => load()}
        />
      )}
      <DriverDialog
        isOpen={editing !== undefined}
        driver={editing}
        onClose={() => setEditing(undefined)}
        onSaved={(d) => {
          setNotice(
            editing ? `Saved ${d?.name || 'the driver'}.` : `Added ${d?.name || 'the driver'}.`
          );
          setReloadKey((k) => k + 1);
        }}
      />
      <ImportDialog
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={(res) => {
          setNotice(`Imported drivers: ${res.created ?? 0} new, ${res.updated ?? 0} updated.`);
          setReloadKey((k) => k + 1);
        }}
      />
    </div>
  );
};

export default Drivers;
